<template>
  <iColumn gap="standard" width="fill">
    <div class="vibe-grid">
      <button
        v-for="option in previewOptions"
        :key="option.value"
        type="button"
        class="vibe-card"
        :class="{'vibe-card-selected': value === option.value}"
        :style="cardStyle(option)"
        @click="selectOption(option.value)"
      >
        <span class="vibe-card-sample" :style="sampleStyle(option)">Aa</span>
        <span class="vibe-card-label" :style="labelStyle(option)">{{ option.label }}</span>
        <span class="vibe-card-swatches">
          <span
            v-for="(swatch, swatchIndex) in option.swatches"
            :key="swatchIndex"
            class="vibe-card-swatch"
            :style="{backgroundColor: swatch}"
          />
        </span>
      </button>
    </div>

    <iRow
      v-if="otherOption"
      align="left"
      gap="extraSmall"
      wrap="wrap"
    >
      <iButton
        :variant="value === otherOption.value ? 'primary' : 'secondary'"
        width="hug"
        @click="selectOption(otherOption.value)"
      >
        {{ otherOption.label }}
      </iButton>
    </iRow>
  </iColumn>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { useQuestionnaireStore } from "@/stores/questionnaire-store";

// WCAG large-text contrast floor - the "Aa" sample is 36px bold, so 3:1 is the
// readability threshold we hold accent colours to against the card background.
const LARGE_TEXT_MIN_CONTRAST = 3;
const READABLE_DARK = "#1a1a1a";
const READABLE_LIGHT = "#ffffff";

function parseHexColor(hex) {
  if (typeof hex !== "string") {
    return null;
  }
  let value = hex.trim().replace("#", "");
  if (value.length === 3) {
    value = value.split("").map(character => character + character).join("");
  }
  if (value.length !== 6 || /[^0-9a-fA-F]/.test(value)) {
    return null;
  }
  return {
    red: parseInt(value.slice(0, 2), 16),
    green: parseInt(value.slice(2, 4), 16),
    blue: parseInt(value.slice(4, 6), 16),
  };
}

function channelLuminance(channelValue) {
  const normalized = channelValue / 255;
  return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(color) {
  const redComponent = 0.2126 * channelLuminance(color.red);
  const greenComponent = 0.7152 * channelLuminance(color.green);
  const blueComponent = 0.0722 * channelLuminance(color.blue);
  return redComponent + greenComponent + blueComponent;
}

function contrastRatio(hexA, hexB) {
  const colorA = parseHexColor(hexA);
  const colorB = parseHexColor(hexB);
  if (!colorA || !colorB) {
    return 1;
  }
  const luminanceA = relativeLuminance(colorA);
  const luminanceB = relativeLuminance(colorB);
  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);
  return (lighter + 0.05) / (darker + 0.05);
}

// Black or white, whichever reads better on the given background.
function readableTextColor(backgroundHex) {
  return contrastRatio(backgroundHex, READABLE_DARK) >= contrastRatio(backgroundHex, READABLE_LIGHT)
    ? READABLE_DARK
    : READABLE_LIGHT;
}

export default {
  name: "QuestionnaireVibePicker",
  props: {
    sectionKey: {
      type: String,
      required: true,
    },
    field: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState(useQuestionnaireStore, ["responseData"]),
    value() {
      return (this.responseData[this.sectionKey] || {})[this.field.key];
    },
    previewOptions() {
      return (this.field.options || []).filter(option => option.value !== "other");
    },
    otherOption() {
      return (this.field.options || []).find(option => option.value === "other") || null;
    },
  },
  methods: {
    ...mapActions(useQuestionnaireStore, ["setSingleSelectValue"]),
    selectOption(optionValue) {
      this.setSingleSelectValue(this.sectionKey, this.field.key, optionValue);
    },
    cardBackground(option) {
      const swatches = Array.isArray(option.swatches) ? option.swatches : [];
      return swatches[0] || "#ffffff";
    },
    cardStyle(option) {
      return { backgroundColor: this.cardBackground(option) };
    },
    // Keep the intended accent swatch when it is legible on the card; otherwise
    // fall back to the most-contrasting swatch, then to plain black/white.
    accentColorForSample(option) {
      const background = this.cardBackground(option);
      const accentSwatches = (Array.isArray(option.swatches) ? option.swatches : []).slice(1);
      const preferredAccent = accentSwatches[0];
      if (preferredAccent && contrastRatio(background, preferredAccent) >= LARGE_TEXT_MIN_CONTRAST) {
        return preferredAccent;
      }
      let bestAccent = "";
      let bestContrast = 0;
      for (const swatch of accentSwatches) {
        const ratio = contrastRatio(background, swatch);
        if (ratio > bestContrast) {
          bestContrast = ratio;
          bestAccent = swatch;
        }
      }
      if (bestAccent && bestContrast >= LARGE_TEXT_MIN_CONTRAST) {
        return bestAccent;
      }
      return readableTextColor(background);
    },
    sampleStyle(option) {
      return {
        color: this.accentColorForSample(option),
        fontFamily: option.font_family || "inherit",
      };
    },
    // The label stays in the readable UI font (the sample already showcases the
    // vibe's typeface) and takes a guaranteed-contrast colour.
    labelStyle(option) {
      return { color: readableTextColor(this.cardBackground(option)) };
    },
  },
  styleGuide: () => ({
    brandedColor: { "color.font": "branded" },
  }),
};
</script>

<style scoped lang="scss">
.vibe-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    width: 100%;
}

@media (max-width: 640px) {
    .vibe-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

.vibe-card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 20px;
    min-height: 160px;
    border: 2px solid var(--i-color-border, #d9d9d9);
    border-radius: 8px;
    background-color: #fff;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
    text-align: left;

    &:hover {
        border-color: v-bind('$getStyles.brandedColor');
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }
}

.vibe-card-selected {
    border-color: v-bind('$getStyles.brandedColor');
    border-width: 4px;
    box-shadow: 0 0 0 2px v-bind('$getStyles.brandedColor');
}

.vibe-card-sample {
    font-size: 36px;
    line-height: 1;
    font-weight: 700;
}

.vibe-card-label {
    font-size: 15px;
    font-weight: 600;
    line-height: 1.3;
}

.vibe-card-swatches {
    display: inline-flex;
    gap: 4px;
    align-self: stretch;
}

.vibe-card-swatch {
    flex: 1;
    height: 8px;
    border: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
